/// <amd-module name="Repos/Tfvc/Common/TfvcRoutingService" />
import { getTFSProject, IProjectData } from "Tfs/Platform/Page";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { routeUrl } from "VSS/Platform/Location";

const enum RouteIds {
    FilesHub = "ms.vss-code-web.tfvc-files-route",

    ChangesetDetails = "ms.vss-code-web.tfvc-changeset-details-route",
    ChangesetsHub = "ms.vss-code-web.tfvc-changesets-hub-route",

    ShelvesetDetails = "ms.vss-code-web.tfvc-shelveset-details-route",
    ShelvesetsHub = "ms.vss-code-web.tfvc-shelvesets-hub-route"
}

export interface ITfvcRoutingService extends IVssService {
    /**
     * Gets the URL of the changesets hub.
     * If no project specified, current project is used.
     *
     * @param projectName Name of the project
     */
    getChangesetsHubUrl(projectName?: string): string;

    /**
     * Gets the URL of the details page for the given changeset Id.

     * @param changesetId ID of the changeset.
     * @param projectName Name of the project.
     * @param path Path of the file in the changeset.
     */
    getChangesetDetailsUrl(changesetId: number, projectName?: string, path?: string): string;

    /**
     * Gets the URL of the files hub.
     * If no project specified, collection level files hub url is generated.
     *
     * @param path Path of the file
     * @param version Version string or changeset to display
     */
    getFilesHubUrl(path?: string, version?: string): string;

    /**
     * Gets the URL of the files hub for the given path and version in the annotate tab.

     * @param path Path of the file
     * @param version Version string or changeset to annotate
     */
    getFilesAnnotateUrl(path: string, version: string): string;

    /**
     * Gets the URL of the files hub for the given path and version in the history tab.
     *
     * @param path Path of the file
     * @param version Version string or changeset to display
     */
    getFilesHistoryUrl(path: string, version: string): string;

    /**
     * Gets the URL of the files hub for the given path and version in the compare tab.
     *
     * @param path Path of the file
     * @param version Version string or changeset to display
     * @param oversion Version to compare the path to
     */
    getFilesCompareUrl(path: string, version: string, oversion: string): string;

    /**
     * Gets the URL of the details page for the given shelveset name.

     * @param shelvesetName Name of the shelveset.
     * @param projectName Name of the project.
     * @param path Path of the file in the shelveset.
     */
    getShelvesetDetailsUrl(shelvesetName: string, projectName?: string, path?: string): string;

    /**
     * Gets the URL of the shelvesets hub.
     * If no project specified, current project is used.
     *
     * @param projectName Name of the project
     */
    getShelvesetsHubUrl(projectName?: string): string;
}

class TfvcRoutingService extends VssService implements ITfvcRoutingService {
    private projectInfo: IProjectData;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);

        this.projectInfo = getTFSProject(pageContext)!;
    }

    public getChangesetsHubUrl(projectName?: string): string {
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = projectName || this.projectInfo.name;

        return routeUrl(this.pageContext, RouteIds.ChangesetsHub, routeValues);
    }

    public getChangesetDetailsUrl(changesetId: number, projectName?: string, path?: string): string {
        const routeValues: { [key: string]: string } = {};

        routeValues["parameters"] = changesetId.toString();
        routeValues["project"] = projectName || this.projectInfo.name;

        if (path) {
            routeValues["path"] = path;
        }

        return routeUrl(this.pageContext, RouteIds.ChangesetDetails, routeValues);
    }

    public getFilesHubUrl(path?: string, version?: string): string {
        const routeValues: { [key: string]: string } = {};

        // If path belongs to a different project, navigate to it
        routeValues["project"] = getProjectFromPath(path) || this.projectInfo.name;

        if (path) {
            routeValues["path"] = path;
        }

        if (version) {
            routeValues["version"] = version;
        }

        return routeUrl(this.pageContext, RouteIds.FilesHub, routeValues);
    }

    public getFilesAnnotateUrl(path: string, version: string): string {
        return routeUrl(this.pageContext, RouteIds.FilesHub, {
            path,
            project: getProjectFromPath(path) || this.projectInfo.name,
            version,
            _a: "annotate"
        });
    }

    public getFilesHistoryUrl(path: string, version: string): string {
        return routeUrl(this.pageContext, RouteIds.FilesHub, {
            path,
            project: getProjectFromPath(path) || this.projectInfo.name,
            version,
            _a: "history"
        });
    }

    public getFilesCompareUrl(path: string, version: string, oversion: string): string {
        return routeUrl(this.pageContext, RouteIds.FilesHub, {
            path,
            project: getProjectFromPath(path) || this.projectInfo.name,
            version,
            _a: "compare",
            oversion,
            mversion: version
        });
    }

    public getShelvesetDetailsUrl(shelvesetName: string, projectName?: string, path?: string): string {
        const routeValues: { [key: string]: string } = {};

        routeValues["ss"] = shelvesetName;
        routeValues["project"] = projectName || this.projectInfo.name;

        if (path) {
            routeValues["path"] = path;
        }

        return routeUrl(this.pageContext, RouteIds.ShelvesetDetails, routeValues);
    }

    public getShelvesetsHubUrl(projectName?: string): string {
        const routeValues: { [key: string]: string } = {};
        routeValues["project"] = projectName || this.projectInfo.name;

        return routeUrl(this.pageContext, RouteIds.ShelvesetsHub, routeValues);
    }
}

function getProjectFromPath(path: string | undefined): string | undefined {
    // Extract ProjectName from path like $/ProjectName/file.txt
    return path && path.split("/")[1];
}

const serviceName = "ITfvcRoutingService";

export function getTfvcRoutingService(pageContext: IVssPageContext): ITfvcRoutingService {
    return pageContext.getService<ITfvcRoutingService>(serviceName);
}

Services.add(serviceName, { serviceFactory: TfvcRoutingService });
